// src/screens/s2/ValueGapCalculator.tsx
// ─────────────────────────────────────────────────────────────────────────────
// Session 2 Screen 9 — Value Gap Calculator.
// Two sections of levers (profit + cash), each with a current value from
// Xero and a target input in local state. Combined output recomputes on
// every change: incremental EBITDA, CT saving, pension headroom, new-client
// target, lead-generation ask, freedom-age movement, business-EF completion.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useMemo } from 'react';
import { ClientData } from '../../types/ClientData';
import { useXeroData } from '../../hooks/useXeroData';
import { useXeroSales } from '../../hooks/useXeroSales';
import { useXeroCashflow } from '../../hooks/useXeroCashflow';
import { effectiveCTRate } from '../../taxConfig';
import { formatCurrency, formatClients, formatPercent, formatAge } from '../../utils/formatters';

interface Props {
  data: ClientData;
  onUpdate: (updates: Partial<ClientData>) => void;
}

interface LeverTargets {
  // Profit levers
  leadsPerMonth: number;
  conversionRate: number;       // %
  avgClientValueUplift: number; // % uplift over current
  transactionFrequency: number; // times per client per year
  retentionRate: number;        // %
  cogsPct: number;              // % of revenue
  overheadAnnual: number;       // £ annual
  // Cash levers
  debtorDays: number;
  creditorDays: number;
  wipDays: number;
  debtStructureNote: string;
}

function LeverRow({
  label, current, target, onChangeTarget, impact, suffix = '',
}: {
  label: string; current: string | number; target: number;
  onChangeTarget: (v: number) => void; impact: string; suffix?: string;
}) {
  return (
    <div className="grid grid-cols-4 gap-3 py-3 border-b border-gray-100 last:border-0 items-center">
      <div className="text-sm font-medium text-gray-700">{label}</div>
      <div className="text-sm text-gray-500">
        {typeof current === 'number' ? current.toLocaleString('en-GB') : current}{suffix}
      </div>
      <div className="flex items-baseline gap-1">
        <input
          type="number"
          value={target}
          onChange={(e) => onChangeTarget(parseFloat(e.target.value || '0') || 0)}
          className="text-sm font-semibold text-ma-navy bg-transparent border border-gray-300 rounded px-2 py-1 w-full"
        />
        {suffix && <span className="text-xs text-gray-500">{suffix}</span>}
      </div>
      <div className="text-sm font-semibold text-emerald-700">{impact}</div>
    </div>
  );
}

export default function ValueGapCalculator({ data }: Props) {
  const xero = useXeroData(data.xeroTenantId);
  const sales = useXeroSales(data.xeroTenantId);
  const cashflow = useXeroCashflow(data.xeroTenantId);

  // All hooks must fire unconditionally before any early-return branch.
  // ── Default targets derived from Xero baseline ─────────────────────────
  const [targets, setTargets] = useState<LeverTargets>(() => ({
    leadsPerMonth: data.s2LeadsPerMonth ?? 0,
    conversionRate: data.s2ConversionRate ?? 0,
    avgClientValueUplift: 0,
    transactionFrequency: 0,
    retentionRate: 0,
    cogsPct: 0,
    overheadAnnual: 0,
    debtorDays: 0,
    creditorDays: 0,
    wipDays: 0,
    debtStructureNote: '',
  }));

  // ── Baselines — computed unconditionally so they can feed the useMemo
  // that follows. All reads are null-safe; when Xero isn't loaded yet these
  // resolve to zeros and the render path is replaced by the early-return
  // branch below anyway.
  const s = xero.summary;
  const sd = sales.data;
  const clientCount = data.businessClientCount || sd?.activeClients || 0;
  const avgClientValue = s && clientCount > 0 ? s.revenue / clientCount : 0;
  const currentRetention = sd?.retentionRate ?? 0;
  const currentCogsPct = s && s.revenue > 0 ? (s.costOfSales / s.revenue) * 100 : 0;
  const currentOverheadAnnual = s ? s.totalOperating + s.staffCosts : 0;
  const currentDso = cashflow.data?.dso ?? 0;
  const currentDpo = cashflow.data?.dpo ?? 0;

  const effTargets: LeverTargets = {
    ...targets,
    retentionRate: targets.retentionRate || currentRetention,
    cogsPct: targets.cogsPct || currentCogsPct,
    overheadAnnual: targets.overheadAnnual || currentOverheadAnnual,
    debtorDays: targets.debtorDays || currentDso,
    creditorDays: targets.creditorDays || currentDpo,
  };

  // ── Impact useMemo — always called, returns zeros until Xero is ready ─
  const impacts = useMemo(() => {
    const zeros = { lead: 0, avgValue: 0, retention: 0, cogs: 0, overhead: 0, totalProfit: 0, dsoCashOneOff: 0, dpoCashOneOff: 0, totalCashOneOff: 0 };
    if (!s) return zeros;
    const leadImpact = (effTargets.leadsPerMonth * (effTargets.conversionRate / 100) * 12) * avgClientValue;
    const avgValueImpact = s.revenue * (effTargets.avgClientValueUplift / 100);
    const freqImpact = (effTargets.transactionFrequency || 0) * avgClientValue * clientCount * 0;  // needs more context — omit for now
    const retentionUplift = Math.max(0, effTargets.retentionRate - currentRetention);
    const retentionImpact = s.revenue * (retentionUplift / 100);
    const cogsDelta = Math.max(0, currentCogsPct - effTargets.cogsPct);
    const cogsImpact = s.revenue * (cogsDelta / 100);
    const overheadImpact = Math.max(0, currentOverheadAnnual - effTargets.overheadAnnual);
    const totalProfit = leadImpact + avgValueImpact + freqImpact + retentionImpact + cogsImpact + overheadImpact;

    const dsoReduction = Math.max(0, currentDso - effTargets.debtorDays);
    const dpoIncrease = Math.max(0, effTargets.creditorDays - currentDpo);
    const dsoCashOneOff = (dsoReduction / 365) * s.revenue;
    const dpoCashOneOff = (dpoIncrease / 365) * s.costOfSales;
    const totalCashOneOff = dsoCashOneOff + dpoCashOneOff;

    return {
      lead: leadImpact, avgValue: avgValueImpact, retention: retentionImpact,
      cogs: cogsImpact, overhead: overheadImpact, totalProfit,
      dsoCashOneOff, dpoCashOneOff, totalCashOneOff,
    };
  }, [effTargets, s, avgClientValue, clientCount, currentRetention, currentCogsPct, currentOverheadAnnual, currentDso, currentDpo]);

  // Non-hook helper — safe to declare anywhere.
  const update = (patch: Partial<LeverTargets>) => setTargets((t) => ({ ...t, ...patch }));

  const loading = xero.loading || sales.loading || cashflow.loading;
  if (loading) {
    return <div className="p-6 max-w-6xl mx-auto text-sm text-gray-500">Loading value-gap baseline…</div>;
  }
  if (!xero.mapped || !s) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <h2 className="text-2xl font-bold text-ma-navy mb-4">Value Gap Calculator</h2>
        <div className="rounded-2xl bg-amber-50 border-2 border-amber-300 p-5 text-sm text-amber-800">
          {xero.error || 'This lever calculator requires live Xero P&L data.'}
        </div>
      </div>
    );
  }

  // ── Combined output ────────────────────────────────────────────────────
  const projectedProfit = s.netProfit + impacts.totalProfit;
  const newCtRate = effectiveCTRate(projectedProfit);
  const currentCtRate = effectiveCTRate(s.netProfit);
  const additionalCt = impacts.totalProfit * newCtRate;     // incremental CT exposure
  const additionalPensionHeadroom = (impacts.totalProfit * (1 - newCtRate)) / 12;

  // Client acquisition target to cover additional profit goal
  const clientsNeeded = avgClientValue > 0 ? impacts.totalProfit / avgClientValue : 0;
  const leadsNeeded = effTargets.conversionRate > 0
    ? clientsNeeded / (effTargets.conversionRate / 100)
    : 0;

  // Freedom-age movement (proxy): incremental profit → pension pot at 4% drawdown
  const gapBefore = data.s2GapBefore ?? data.gapAmount ?? 0;
  const clientAge = data.clientAge || 40;
  const freedomAge = data.targetFreedomAge || 55;
  const yearsToFreedom = Math.max(0, freedomAge - clientAge);
  const annualIncomeContribution = additionalPensionHeadroom * 12 * 0.04 * yearsToFreedom;
  const gapClosedYears = gapBefore > 0 && annualIncomeContribution > 0
    ? Math.min(yearsToFreedom, Math.round((annualIncomeContribution / gapBefore) * yearsToFreedom))
    : 0;

  // Business EF reached — shave months from CapitalAllocation's EF build.
  const monthlyStaff = s.staffCosts / 12;
  const monthlyOperating = s.totalOperating / 12;
  const monthlyOwnerSalary = s.ownerSalary / 12;
  const fixedCostsMonthly = monthlyStaff + monthlyOperating + monthlyOwnerSalary;
  const efTargetMonths = data.s2BusinessEfTarget ?? 3;
  const efTarget = fixedCostsMonthly * efTargetMonths;
  const efCurrent = (data.businessOperatingBufferBalance || 0) + impacts.totalCashOneOff;
  const efGap = Math.max(0, efTarget - efCurrent);
  const efMonthsImproved = additionalPensionHeadroom > 0 ? Math.ceil(efGap / additionalPensionHeadroom) : 0;

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-ma-navy">
          Value Gap Calculator
          {xero.tenantName && <span className="ml-2 text-sm font-normal text-gray-500">— {xero.tenantName}</span>}
        </h2>
        <p className="text-gray-500 mt-1 text-sm">
          Model the revenue, margin and cash levers that would close the remaining gap.
        </p>
      </div>

      {/* SECTION A — PROFIT LEVERS */}
      <h3 className="text-sm font-bold text-ma-navy uppercase tracking-wide mb-3">Profit levers</h3>
      <div className="bg-white rounded-2xl border border-gray-200 p-5 mb-6">
        <div className="grid grid-cols-4 gap-3 pb-2 mb-1 border-b-2 border-gray-200 text-xs uppercase tracking-wide text-gray-400">
          <div>Lever</div>
          <div>Current</div>
          <div>Target</div>
          <div>Annual EBITDA impact</div>
        </div>
        <LeverRow label="Leads per month"         current={data.s2LeadsPerMonth ?? '—'}
                  target={effTargets.leadsPerMonth} onChangeTarget={(v) => update({ leadsPerMonth: v })}
                  impact={formatCurrency(impacts.lead)} />
        <LeverRow label="Conversion rate"         current={data.s2ConversionRate ? `${data.s2ConversionRate}%` : '—'}
                  target={effTargets.conversionRate} onChangeTarget={(v) => update({ conversionRate: v })}
                  impact={impacts.lead > 0 ? 'see leads above' : '—'}  suffix="%" />
        <LeverRow label="Avg client value uplift" current={formatCurrency(avgClientValue)}
                  target={effTargets.avgClientValueUplift} onChangeTarget={(v) => update({ avgClientValueUplift: v })}
                  impact={formatCurrency(impacts.avgValue)} suffix="%" />
        <LeverRow label="Transaction frequency"   current="—"
                  target={effTargets.transactionFrequency} onChangeTarget={(v) => update({ transactionFrequency: v })}
                  impact="—" suffix="/yr" />
        <LeverRow label="Retention rate"          current={`${currentRetention}%`}
                  target={effTargets.retentionRate} onChangeTarget={(v) => update({ retentionRate: v })}
                  impact={formatCurrency(impacts.retention)} suffix="%" />
        <LeverRow label="Cost of sales %"         current={formatPercent(currentCogsPct)}
                  target={effTargets.cogsPct} onChangeTarget={(v) => update({ cogsPct: v })}
                  impact={formatCurrency(impacts.cogs)} suffix="%" />
        <LeverRow label="Overhead £ annual"       current={formatCurrency(currentOverheadAnnual)}
                  target={effTargets.overheadAnnual} onChangeTarget={(v) => update({ overheadAnnual: v })}
                  impact={formatCurrency(impacts.overhead)} />
      </div>

      {/* SECTION B — CASH LEVERS */}
      <h3 className="text-sm font-bold text-ma-navy uppercase tracking-wide mb-3">Cash levers</h3>
      <div className="bg-white rounded-2xl border border-gray-200 p-5 mb-6">
        <div className="grid grid-cols-4 gap-3 pb-2 mb-1 border-b-2 border-gray-200 text-xs uppercase tracking-wide text-gray-400">
          <div>Lever</div>
          <div>Current</div>
          <div>Target</div>
          <div>Cash released</div>
        </div>
        <LeverRow label="Debtor days (DSO)"    current={`${currentDso} days`}
                  target={effTargets.debtorDays} onChangeTarget={(v) => update({ debtorDays: v })}
                  impact={formatCurrency(impacts.dsoCashOneOff)} suffix="d" />
        <LeverRow label="Creditor days (DPO)"  current={`${currentDpo} days`}
                  target={effTargets.creditorDays} onChangeTarget={(v) => update({ creditorDays: v })}
                  impact={formatCurrency(impacts.dpoCashOneOff)} suffix="d" />
        <LeverRow label="WIP / inventory days" current="—"
                  target={effTargets.wipDays} onChangeTarget={(v) => update({ wipDays: v })}
                  impact="—" suffix="d" />
        <div className="pt-3">
          <div className="text-xs font-medium text-gray-500 mb-2">Debt / capital structure — adviser note</div>
          <textarea
            rows={2}
            value={effTargets.debtStructureNote}
            onChange={(e) => update({ debtStructureNote: e.target.value })}
            placeholder="e.g. refinance bounce-back loan, renegotiate hire purchase…"
            className="w-full text-sm border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-ma-navy"
          />
        </div>
      </div>

      {/* COMBINED OUTPUT */}
      <h3 className="text-sm font-bold text-ma-navy uppercase tracking-wide mb-3">Combined output</h3>
      <div className="rounded-2xl p-6 mb-6 text-white" style={{ background: 'linear-gradient(135deg, #1B2B4B 0%, #2a3f6b 100%)' }}>
        <div className="grid grid-cols-3 gap-6 mb-6">
          <div>
            <div className="text-xs uppercase tracking-wide opacity-80 mb-1">Additional annual profit</div>
            <div className="text-3xl font-bold" style={{ color: '#C9A84C' }}>{formatCurrency(impacts.totalProfit)}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide opacity-80 mb-1">Additional CT saving</div>
            <div className="text-2xl font-bold">{formatCurrency(additionalCt)}</div>
            <div className="text-xs opacity-75 mt-1">Effective rate moves {formatPercent(currentCtRate * 100)} → {formatPercent(newCtRate * 100)}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide opacity-80 mb-1">Additional pension headroom</div>
            <div className="text-2xl font-bold">{formatCurrency(additionalPensionHeadroom)}/mo</div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-6">
          <div>
            <div className="text-xs uppercase tracking-wide opacity-80 mb-1">Client acquisition target</div>
            <div className="text-xl font-bold">{formatClients(clientsNeeded)} new clients</div>
            <div className="text-xs opacity-75 mt-1">@ {formatCurrency(avgClientValue)} avg fee</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide opacity-80 mb-1">Lead generation needed</div>
            <div className="text-xl font-bold">{formatClients(leadsNeeded)} leads/mo</div>
            <div className="text-xs opacity-75 mt-1">At {effTargets.conversionRate || 0}% conversion</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide opacity-80 mb-1">Freedom age gained</div>
            <div className="text-xl font-bold" style={{ color: '#C9A84C' }}>
              {gapClosedYears} year{gapClosedYears === 1 ? '' : 's'} earlier
            </div>
            <div className="text-xs opacity-75 mt-1">
              Currently: age {formatAge(freedomAge)}
            </div>
          </div>
        </div>

        <div className="mt-6 pt-6 border-t border-white/15 grid grid-cols-2 gap-6">
          <div>
            <div className="text-xs uppercase tracking-wide opacity-80 mb-1">Cash released (one-off)</div>
            <div className="text-xl font-bold">{formatCurrency(impacts.totalCashOneOff)}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide opacity-80 mb-1">Business EF reached in</div>
            <div className="text-xl font-bold">
              {efGap === 0 ? 'Already met' : efMonthsImproved > 0 ? `${efMonthsImproved} months` : '—'}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
